import React, { Suspense } from 'react';
import { Navigate, Routes, Route } from 'react-router-dom';
import DefaultLayout from './components/layout/DefaultLayout';
import LoginPage from './pages/login/LoginPage';
import RoleProtectedRoute from './RoleProtectedRoute';
import routes from './routes';
import LegacyRedirect from './routes/LegacyRedirect';
import { legacyRedirectRoutes } from './routes/LegacyRedirects';
import {
  ASSETS_PATH,
  assetPath,
  FINDING_CREATE_PATH,
  findingPath,
  findingsPath
} from './routes/paths';
import CreateWorkspace from './pages/workspaces/createWorkspace';
import ListWorkspace from './pages/workspaces/createWorkspace copy';
import AcceptInvitation from './pages/auth/AcceptInvitation';
import AccessInvitation from './pages/auth/AccessInvitation';
import ForgotPassword from './pages/auth/ForgotPassword';
import TwoFactorAuth from './pages/auth/2fa';
import SecurityGate from './components/features/security/SecurityGate';
import Licence from './pages/auth/Licence';
import OidcCallback from './pages/auth/OidcCallback';
import OidcSilentRenew from './pages/auth/OidcSilentRenew';
import PlatformLayout from './components/layout/PlatformLayout';
import ProfileLayout from './components/layout/ProfileLayout';

const KnowledgeV2 = React.lazy(() => import('./pages/knowledgeV2'));
const PlatformAdministration = React.lazy(() => import('./pages/platform'));
const PlatformAccess = React.lazy(() => import('./pages/platform/Access'));
const ProfilePage = React.lazy(() => import('./pages/profile'));
const PlatformAuditLogs = React.lazy(() => import('./pages/platform/AuditLogs'));

function AppContent() {
  return (
    <>
      {/* Outside `<Routes>` on purpose: the two blocking popups it raises have to survive
        navigation, including to `/workspaces` and `/workspace/create`, which sit outside
        `DefaultLayout`. It renders nothing on the sign-in and recovery screens. */}
      <SecurityGate />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {legacyRedirectRoutes()}
        <Route path="/auth/callback" element={<OidcCallback />} />
        <Route path="/auth/silent-renew" element={<OidcSilentRenew />} />
        {/* The trial-ended screen. Public, because nobody can sign in once a licence has
          lapsed and the key is applied here. `service/api.tsx` sends every 402 from the
          licence gate to it. */}
        <Route path="/licence" element={<Licence />} />
        {/* Emails sent before invitations were unified link here. It forwards to the page
          that answers an invitation now, so those links keep working. */}
        <Route path="/accept-invitation" element={<AcceptInvitation />} />
        <Route path="/invitations/:token" element={<AccessInvitation />} />
        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        {/* Where the "ask an administrator" dead end used to live. Kept pointing at the real
          flow so an old bookmark still lands somewhere that works. */}
        <Route path="/auth/reset" element={<ForgotPassword />} />
        {/* `/auth/reset-password?token=…` is gone with the flow that produced the link:
          recovery is a mailed code redeemed for a short-lived ticket now, not a link. */}
        <Route path="/2fa" element={<TwoFactorAuth />} />
        <Route
          path="/_internal/knowledge-v2"
          element={
            <Suspense fallback="loading">
              <KnowledgeV2 />
            </Suspense>
          }
        />
        <Route
          path="/admin"
          element={
            <RoleProtectedRoute
              requiredRoles={['PLATFORM_OWNER', 'SUPPORT_ADMIN']}
            >
              <PlatformLayout />
            </RoleProtectedRoute>
          }
        >
          <Route
            index
            element={
              <Suspense fallback="loading">
                <PlatformAdministration />
              </Suspense>
            }
          />
          <Route
            path="organisations"
            element={
              <Suspense fallback="loading">
                <PlatformAdministration />
              </Suspense>
            }
          />
          <Route
            path="organisations/:organisationId"
            element={
              <Suspense fallback="loading">
                <PlatformAdministration />
              </Suspense>
            }
          />
          <Route
            path="access"
            element={
              <Suspense fallback="loading">
                <PlatformAccess />
              </Suspense>
            }
          />
          <Route
            path="audit"
            element={
              <Suspense fallback="loading">
                <PlatformAuditLogs />
              </Suspense>
            }
          />
        </Route>
        {/* The profile is the person's, not a workspace's, so it sits outside both consoles'
          layouts and has no sidebar. `/platform/profile` forwards here (see LegacyRedirects). */}
        <Route
          element={
            <RoleProtectedRoute>
              <ProfileLayout />
            </RoleProtectedRoute>
          }
        >
          <Route
            path="/profile"
            element={
              <Suspense fallback="loading">
                <ProfilePage />
              </Suspense>
            }
          />
        </Route>
        <Route element={<DefaultLayout />}>
          <Route
            path="/security/code/overview"
            element={<Navigate to="/security/code/findings" replace />}
          />
          <Route
            path="/security/infrastructure/overview"
            element={
              <Navigate to="/security/infrastructure/findings" replace />
            }
          />
          {/* Asset and finding pages moved from /remediation/ to their sidebar sections;
            the old URLs stay valid for bookmarks and shared links. */}
          <Route
            path="/remediation/vulnerabilities"
            element={<LegacyRedirect to={() => findingsPath()} />}
          />
          <Route
            path="/remediation/vulnerabilities/create"
            element={<LegacyRedirect to={() => FINDING_CREATE_PATH} />}
          />
          <Route
            path="/remediation/vulnerabilities/:id"
            element={<LegacyRedirect to={(id) => findingPath(id!)} />}
          />
          <Route
            path="/remediation/assets"
            element={<LegacyRedirect to={() => ASSETS_PATH} />}
          />
          <Route
            path="/remediation/assets/:id"
            element={<LegacyRedirect to={(id) => assetPath(id!)} />}
          />
          <Route
            path="/imports/history"
            element={<Navigate to="/imports" replace />}
          />
          {routes.map((r, i) => (
            <Route
              key={i}
              path={r.path}
              element={
                // Keyed by path: Code and Infrastructure render the same page component, and
                // without a key React keeps it mounted across the switch, with the other
                // domain's rows and totals still loaded.
                <RoleProtectedRoute
                  key={r.path}
                  requiredRoles={r.requiredRoles}
                >
                  <Suspense fallback="loading">
                    <r.component />
                  </Suspense>
                </RoleProtectedRoute>
              }
            />
          ))}
        </Route>
        <Route>
          <Route
            path="/workspace/create"
            element={
              <RoleProtectedRoute>
                <Suspense fallback="loading">
                  <CreateWorkspace />
                </Suspense>
              </RoleProtectedRoute>
            }
          />
          <Route
            path="/workspaces"
            element={
              <RoleProtectedRoute>
                <Suspense fallback="loading">
                  <ListWorkspace />
                </Suspense>
              </RoleProtectedRoute>
            }
          />
        </Route>
      </Routes>
    </>
  );
}

export default AppContent;
